import { Injectable, inject } from '@angular/core';
import {
  ApiDependencyProviderService,
  CollectionRequest,
  CollectionResponse,
  CreateResourceResponse,
  HttpParams,
} from '@web/common';
import { map, Observable } from 'rxjs';
import { blank, Guid } from '@web/utils';
import { WarehouseReleaseCollectionItemResponse } from '../responses/warehouse-release-collection-item.response';
import { WarehouseReleaseDetailsResponse } from '../responses/warehouse-release-details.response';
import { WarehouseReleaseCreateRequest } from '../requests/warehouse-release-create.request';
import { WarehouseReleaseUpdateRequest } from '../requests/warehouse-release-update.request';
import { WarehouseReleaseProductCollectionItemResponse } from '../responses/warehouse-release-product-collection-item.response';

@Injectable({
  providedIn: 'root',
})
export class WarehouseReleaseApiService {
  private readonly provider = inject(ApiDependencyProviderService);

  /* prettier-ignore */
  public fetchCollection(request: CollectionRequest): Observable<CollectionResponse<WarehouseReleaseCollectionItemResponse>> {
    const path = 'admin/release';
    const url = this.provider.httpUrlFactory.withQueryParams(this.hostUrl(), path, this.provider.httpHelper.collectionRequestToQueryParams(request));
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(map(this.provider.httpHelper.bodyToCollectionResponse(WarehouseReleaseCollectionItemResponse)));
  }

  /* prettier-ignore */
  public fetchDetailsById(id: Guid): Observable<WarehouseReleaseDetailsResponse> {
    const path = `admin/release/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {[HttpParams.Id]: id});
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(map(this.provider.httpHelper.bodyToResponse(WarehouseReleaseDetailsResponse)));
  }

  /* prettier-ignore */
  public create(
    request: WarehouseReleaseCreateRequest,
  ): Observable<CreateResourceResponse> {
    const path = 'admin/release';
    const url = this.provider.httpUrlFactory.simple(this.hostUrl(), path);
    const body = this.provider.httpHelper.requestToBody(request, WarehouseReleaseCreateRequest);
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http
      .post(url, body, { context })
      .pipe(map(this.provider.httpHelper.bodyToResponse(CreateResourceResponse)));
  }

  /* prettier-ignore */
  public update(id: Guid, request: WarehouseReleaseUpdateRequest): Observable<void> {
    const path = `admin/release/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, { [HttpParams.Id]: id });
    const body = this.provider.httpHelper.requestToBody(request, WarehouseReleaseUpdateRequest);
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http.put(url, body, { context }).pipe(blank());
  }

  public fetchProductCollection(
    id: Guid,
    request: CollectionRequest,
  ): Observable<
    CollectionResponse<WarehouseReleaseProductCollectionItemResponse>
  > {
    const path = `admin/release/${HttpParams.Id}/good`;
    const url = this.provider.httpUrlFactory.complex((builder) =>
      builder
        .path(path)
        .host(this.hostUrl())
        .params({ [HttpParams.Id]: id })
        .queryParams(
          this.provider.httpHelper.collectionRequestToQueryParams(request),
        )
        .build(),
    );
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            WarehouseReleaseProductCollectionItemResponse,
          ),
        ),
      );
  }

  public approveById(id: Guid): Observable<void> {
    const path = `admin/release/${HttpParams.Id}/approve`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });
    const context = this.provider.httpContextBuilder.build();
    const body = this.provider.httpHelper.emptyBody();

    return this.provider.http.put(url, body, { context }).pipe(blank());
  }

  private hostUrl(): string {
    return this.provider.environment.ApiHostUrl.Warehouse;
  }
}
